import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

export default function AboutSteve() {
  useSeo({
    title: "Steve Bird — Author of the Value Realisation Framework | Langwood360",
    description: "For thirty years in UK boardrooms, Steve Bird has watched organisations create and lose enterprise value. The Value Realisation Framework is the distillation of that work.",
    path: "/steve",
  });
  return (
    <div className="min-h-screen bg-linen">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <img src="https://customer-assets.emergentagent.com/job_vri-index/artifacts/ulxhrnaj_SBPicture1.jpg" alt="Steve Bird" className="w-full h-[600px] object-cover object-top" />
          <div className="tick-label mt-3">Steve Bird · Founder, The Value Realisation Framework</div>
        </div>
        <div className="lg:col-span-7">
          <div className="tick-label-accent mb-6">The person behind the framework</div>
          <h1 className="display-serif text-5xl md:text-7xl">Steve Bird.</h1>
          <div className="rule-orange my-8" />
          <p className="text-lg leading-relaxed text-charcoal">
            Steve has spent more than forty years building, leading, investing
            in and advising organisations across the UK, including listed
            companies, private equity-backed businesses, family enterprises
            and charities. Throughout that time, he has witnessed
            organisations create extraordinary value — and others quietly
            lose it through misaligned leadership, unclear strategy and
            inconsistent execution.
          </p>
          <p className="mt-6 text-lg leading-relaxed text-charcoal">
            The insight that emerged was simple: value is rarely won or lost
            in a transaction. It is built — or quietly eroded — through
            thousands of leadership decisions made long before that moment
            arrives.
          </p>
          <p className="mt-6 text-lg leading-relaxed text-charcoal">
            The Value Realisation Framework is the
            distillation of those experiences. It captures the principles
            that Steve has consistently seen determine whether organisations
            strengthen or weaken over time. Rather than introducing another
            management model, the Framework helps leaders ask better
            questions, make better decisions and create the conditions in
            which organisations become stronger, more valuable and create
            more strategic choices for the future.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-8">
            <div>
              <div className="font-mono text-4xl text-orange">40<span className="text-2xl">yrs</span></div>
              <div className="tick-label mt-2">Building, leading, advising</div>
            </div>
            <div>
              <div className="font-mono text-4xl text-orange">200+</div>
              <div className="tick-label mt-2">Board engagements</div>
            </div>
          </div>
          <div className="mt-12">
            <Link to="/diagnostic" data-testid="steve-cta-diagnostic" className="btn-orange">Take the Index</Link>
          </div>
        </div>
      </section>

      {/* Working With Me */}
      <section className="border-t border-oxford/10 bg-parchment" data-testid="working-with-me">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="tick-label-accent mb-6">Engagements</div>
            <h2 className="display-serif text-4xl md:text-6xl leading-tight">
              Working <em className="italic">with</em> me.
            </h2>
            <div className="rule-orange my-8" />
            <p className="text-charcoal leading-relaxed max-w-sm">
              The philosophy is mine. The client journey is shared. The
              outcome is always the same — helping leaders build more valuable
              organisations and create more choices for the future.
            </p>
          </div>

          <div className="lg:col-span-8 space-y-8 text-lg leading-relaxed text-charcoal">
            <p>
              Every client engagement is different, but they all begin with
              the same objective: helping leaders realise the true value of
              their organisation.
            </p>
            <p>
              Most of my commercial engagements are introduced through my role
              as a <span className="text-oxford font-medium">Business Growth
              Advisor with Elephants Child</span>, where I work alongside an
              experienced team supporting ambitious business owners on their
              journey from Growth to Exit.
            </p>
            <p>
              Within those engagements, I apply <span className="text-oxford">The Value Realisation Framework</span> —
              my leadership philosophy developed through more than forty
              years of building, leading, investing in and advising
              organisations.
            </p>

            <div className="my-4">
              <div className="grid grid-cols-2 gap-6 pb-3 border-b border-oxford/20">
                <div className="tick-label-accent">Stage</div>
                <div className="tick-label-accent">Purpose</div>
              </div>
              {[
                ["Clarity", "Understand reality."],
                ["Alignment", "Unite around purpose."],
                ["Execution", "Deliver with discipline."],
                ["Value Realisation", "Strengthen organisational value."],
                ["Choice", "Create strategic freedom."],
              ].map(([stage, purpose]) => (
                <div key={stage} className="grid grid-cols-2 gap-6 py-4 border-b border-oxford/10">
                  <div className="display-serif text-2xl text-oxford">{stage}</div>
                  <div className="text-charcoal self-center">{purpose}</div>
                </div>
              ))}
            </div>

            <p>
              Where an engagement requires broader specialist expertise —
              whether corporate finance, funding, acquisitions, exit
              planning, legal, tax or sector knowledge — I work
              collaboratively with the wider Elephants Child team to ensure
              clients receive the right support at every stage of their
              journey.
            </p>
            <p className="italic">
              The philosophy is mine. The client journey is shared. The
              outcome is always the same: helping leaders build more valuable
              organisations and create more choices for the future.
            </p>

            <div className="pt-6 border-t border-oxford/10">
              <div className="tick-label-accent mb-4">In partnership</div>
              <p>
                Value is never created by one adviser. It is realised when
                the right people work together around a shared purpose. That
                is why I work in partnership with <span className="text-oxford font-medium">Elephants Child</span>.
                Their Growth to Exit proposition provides clients with access
                to a broad range of expertise, while <span className="text-oxford">The Value Realisation Framework</span>
                {" "}provides the leadership philosophy that underpins my
                contribution to every engagement.
              </p>
            </div>

            <div className="pt-6 flex flex-wrap gap-4">
              <Link to="/diagnostic" data-testid="working-cta-diagnostic" className="btn-orange">
                Begin with the Index
              </Link>
              <Link to="/board/new" data-testid="working-cta-board" className="btn-ghost">
                Run a board session
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
